import { useCallback, useMemo, useEffect } from 'react';
import { useIntl } from 'react-intl';
import { Form, Radio, Divider, message } from 'antd';
import { Button } from '../../../Button';
import { Input } from '../../../Input';
import { InputNumber } from '../../../InputNumber';
import { DatePicker } from '../../../DatePicker';
import { Label } from 'ui/Label';
import EnumSelect from 'admin/Selects/EnumSelect';
import { StyledApplicationDeliveryForm } from './style';
import { formatDateRange } from '../../../../utils/dateHelpers';
import {
  ApplicationDeliveryCondition,
  ApplicationProductData,
  Listing,
  DeliveryConditionsData,
  StorageUnit,
  DeliveryConditionType,
  DeliveryProductReference,
  ListingProduct,
} from '../../../../types/ListingData';

interface ProductQuantityState {
  listing_product_id: string;
  availableQuantity: number;
  usedQuantity: number;
  remainingQuantity: number;
  product: ListingProduct;
}

interface NewApplicationDeliveryFormProps {
  isEditing?: boolean;
  listingData?: Listing | null;
  storageUnits?: StorageUnit[];
  farmProductNames?: { [key: number]: string };
}

const getAvailableProductQuantity = (
  listingProduct: ListingProduct,
  applicationProduct: ApplicationProductData | undefined
): number => {
  const allowSmallerQuantity = !!listingProduct.allow_smaller_quantity;
  const offerType = applicationProduct?.offer_type || 'full_amount';
  const shouldUseOfferedQuantity =
    allowSmallerQuantity && offerType === 'other_amount';

  if (
    shouldUseOfferedQuantity &&
    applicationProduct?.offered_quantity != null &&
    applicationProduct.offered_quantity > 0
  ) {
    return parseFloat(applicationProduct.offered_quantity.toString());
  }

  return parseFloat((listingProduct.required_quantity || 0).toString());
};

const getProductDisplayName = (
  product: ListingProduct,
  listingProductId: string | undefined,
  availableProducts: ApplicationProductData[],
  farmProductNames: { [key: number]: string } | undefined
): string => {
  const appProduct = availableProducts.find(
    (ap) => String(ap.listing_product_id) === String(listingProductId)
  );
  const selectedName =
    appProduct?.farm_product_id &&
    farmProductNames?.[appProduct.farm_product_id]
      ? farmProductNames[appProduct.farm_product_id]
      : null;
  return selectedName || product.name || product.category?.name || '';
};

export const NewApplicationDeliveryForm = ({
  isEditing = false,
  listingData,
  storageUnits = [],
  farmProductNames = {},
}: NewApplicationDeliveryFormProps) => {
  const intl = useIntl();
  const form = Form.useFormInstance();

  const availableProducts =
    (Form.useWatch('application_products', form) as
      | ApplicationProductData[]
      | undefined) || [];

  const listingProducts = useMemo(() => {
    return listingData?.products || [];
  }, [listingData?.products]);

  const listingDeliveryConditions = useMemo(() => {
    if (!listingData?.delivery_conditions) {
      return [];
    }

    return listingData.delivery_conditions.filter(
      (condition) =>
        condition.delivery_type === 'delivery_conditions' ||
        condition.delivery_type === 'detailed_delivery'
    );
  }, [listingData]);

  const freeFormDeliveryCondition = useMemo(() => {
    if (!listingData?.delivery_conditions) {
      return null;
    }

    return (
      listingData.delivery_conditions.find(
        (condition) => condition.delivery_type === 'free_form'
      ) || null
    );
  }, [listingData]);

  const formDeliveries = Form.useWatch('delivery_conditions', form) as
    | ApplicationDeliveryCondition[]
    | undefined;

  useEffect(() => {
    if (isEditing) {
      return;
    }

    const deliveries = formDeliveries || [];
    const storedDeliveries =
      (form.getFieldValue('delivery_conditions') as
        | ApplicationDeliveryCondition[]
        | undefined) || [];

    if (deliveries.length === 0 && listingDeliveryConditions.length === 0) {
      if (storedDeliveries.length > 0) {
        return;
      }
      let products: { listing_product_id: string; product_quantity: number }[] =
        [];

      if (listingProducts.length > 0 && availableProducts.length > 0) {
        products = listingProducts
          .filter((lp): lp is ListingProduct & { id: string } => !!lp.id)
          .map((lp) => {
            const applicationProduct = availableProducts.find(
              (ap) => ap.listing_product_id === lp.id
            );
            const availableQuantity = getAvailableProductQuantity(
              lp,
              applicationProduct
            );

            return {
              listing_product_id: lp.id,
              product_quantity: isNaN(availableQuantity)
                ? 0
                : availableQuantity,
            };
          })
          .filter((p) => p.product_quantity > 0);
      }

      const defaultDelivery: ApplicationDeliveryCondition = {
        delivery_start_date: undefined,
        delivery_end_date: undefined,
        delivery_price: undefined,
        delivery_frequency_value: undefined,
        delivery_frequency_unit: undefined,
        is_accepting_all: true,
        products: products,
      };
      form.setFieldValue('delivery_conditions', [defaultDelivery]);
    }
  }, [
    form,
    formDeliveries,
    listingDeliveryConditions,
    listingProducts,
    availableProducts,
    isEditing,
  ]);

  const calculateProductQuantities = useCallback(
    (
      listingProducts: ListingProduct[],
      availableProducts: ApplicationProductData[],
      deliveries: ApplicationDeliveryCondition[]
    ): ProductQuantityState[] => {
      const productQuantities: ProductQuantityState[] = [];

      listingProducts.forEach((listingProduct) => {
        if (!listingProduct.id) return;

        const applicationProduct = availableProducts.find(
          (ap) => ap.listing_product_id === listingProduct.id
        );

        const availableQuantity = getAvailableProductQuantity(
          listingProduct,
          applicationProduct
        );

        let usedQuantity = 0;

        listingDeliveryConditions.forEach((condition) => {
          const correspondingDelivery = deliveries.find(
            (delivery) =>
              delivery.listing_delivery_condition_id === condition.id
          );

          if (condition.delivery_type === 'detailed_delivery') {
            if (!correspondingDelivery) {
              return;
            }

            if (correspondingDelivery.is_accepting_all) {
              if (condition.is_all_products) {
                if (
                  condition.products &&
                  Array.isArray(condition.products) &&
                  condition.products.length > 0
                ) {
                  (condition.products as DeliveryProductReference[]).forEach(
                    (p) => {
                      if (p.listing_product_id === listingProduct.id) {
                        const quantity = parseFloat(
                          (
                            p.delivery_quantity ??
                            p.product_quantity ??
                            0
                          ).toString()
                        );
                        usedQuantity += quantity;
                      }
                    }
                  );
                } else {
                  const remaining = Math.max(
                    0,
                    availableQuantity - usedQuantity
                  );
                  usedQuantity += remaining;
                }
              } else if (condition.products) {
                (condition.products as DeliveryProductReference[]).forEach(
                  (p) => {
                    if (p.listing_product_id === listingProduct.id) {
                      const quantity = parseFloat(
                        (
                          p.delivery_quantity ??
                          p.product_quantity ??
                          0
                        ).toString()
                      );
                      usedQuantity += quantity;
                    }
                  }
                );
              }
            } else {
              if (correspondingDelivery.products) {
                correspondingDelivery.products.forEach((p) => {
                  if (p.listing_product_id === listingProduct.id) {
                    const qty = parseFloat(
                      p.product_quantity?.toString() || '0'
                    );
                    usedQuantity += qty;
                  }
                });
              }
            }
          }

          if (condition.delivery_type === 'delivery_conditions') {
            if (
              !correspondingDelivery ||
              correspondingDelivery.is_accepting_all
            ) {
              const remaining = Math.max(0, availableQuantity - usedQuantity);
              usedQuantity += remaining;
            } else {
              if (correspondingDelivery.products) {
                correspondingDelivery.products.forEach((p) => {
                  if (p.listing_product_id === listingProduct.id) {
                    const qty = parseFloat(
                      p.product_quantity?.toString() || '0'
                    );
                    usedQuantity += qty;
                  }
                });
              }
            }
          }
        });

        deliveries.forEach((delivery) => {
          if (
            delivery.listing_delivery_condition_id !== undefined &&
            delivery.listing_delivery_condition_id !== null
          ) {
            return;
          }

          if (delivery.is_accepting_all) {
            const remaining = Math.max(0, availableQuantity - usedQuantity);
            usedQuantity += remaining;
          } else if (delivery.products) {
            delivery.products.forEach((p: DeliveryProductReference) => {
              if (p.listing_product_id === listingProduct.id) {
                const quantity = parseFloat(
                  p.product_quantity?.toString() || '0'
                );
                usedQuantity += quantity;
              }
            });
          }
        });

        const remainingQuantity = Math.max(0, availableQuantity - usedQuantity);

        productQuantities.push({
          listing_product_id: listingProduct.id,
          availableQuantity,
          usedQuantity,
          remainingQuantity,
          product: listingProduct,
        });
      });

      return productQuantities;
    },
    [listingDeliveryConditions]
  );

  const productQuantities = useMemo(() => {
    const deliveries = formDeliveries || [];
    const quantities = calculateProductQuantities(
      listingProducts,
      availableProducts,
      deliveries
    );
    return quantities;
  }, [
    listingProducts,
    availableProducts,
    formDeliveries,
    calculateProductQuantities,
  ]);

  useEffect(() => {
    const deliveries = formDeliveries || [];

    if (
      listingProducts.length === 0 ||
      availableProducts.length === 0 ||
      deliveries.length === 0
    ) {
      return;
    }

    let hasUpdates = false;
    const updatedDeliveries = [...deliveries];

    deliveries.forEach((delivery, index) => {
      if (
        delivery.is_accepting_all &&
        !delivery.listing_delivery_condition_id
      ) {
        const tempDeliveries = [...deliveries];
        tempDeliveries[index] = {
          ...delivery,
          is_accepting_all: true,
          products: [],
        };

        const tempQuantities = calculateProductQuantities(
          listingProducts,
          availableProducts,
          tempDeliveries.filter((_, idx) => idx !== index)
        );

        const productsWithRemaining: {
          listing_product_id: string;
          product_quantity: number;
        }[] = tempQuantities
          .filter((pq) => pq.remainingQuantity > 0 && pq.listing_product_id)
          .map((pq) => ({
            listing_product_id: pq.listing_product_id!,
            product_quantity: pq.remainingQuantity,
          }));

        const productsChanged =
          !delivery.products ||
          delivery.products.length !== productsWithRemaining.length ||
          productsWithRemaining.some((p) => {
            const existing = delivery.products?.find(
              (dp) => dp.listing_product_id === p.listing_product_id
            );
            return (
              !existing ||
              Number(existing.product_quantity) !== Number(p.product_quantity)
            );
          });

        if (productsChanged) {
          updatedDeliveries[index] = {
            ...delivery,
            products: productsWithRemaining,
          };
          hasUpdates = true;
        }
      }
    });

    if (hasUpdates) {
      form.setFieldValue('delivery_conditions', updatedDeliveries);
    }
  }, [
    formDeliveries,
    listingProducts,
    availableProducts,
    form,
    calculateProductQuantities,
  ]);

  const getProductsForDelivery = useCallback(
    (deliveryCondition: DeliveryConditionsData | null) => {
      if (!deliveryCondition) return listingProducts;

      if (
        deliveryCondition.delivery_type === 'detailed_delivery' &&
        deliveryCondition.products
      ) {
        return deliveryCondition.products.map((product) => {
          if ('listing_product_id' in product) {
            const deliveryProduct = product as DeliveryProductReference;
            const productId = deliveryProduct.listing_product_id;
            const listingProduct = listingProducts.find(
              (p) => p.id === productId
            );

            const quantity =
              deliveryProduct.delivery_quantity ??
              deliveryProduct.product_quantity;

            if (listingProduct) {
              return {
                ...listingProduct,
                required_quantity: parseFloat(quantity?.toString() || '0'),
              };
            }

            return {
              id: productId,
              required_quantity: parseFloat(quantity?.toString() || '0'),
              storage_unit: { short: '', name: '' },
              category: {
                name: `Product ${productId}`,
              },
            } as ListingProduct;
          } else {
            return product as ListingProduct;
          }
        });
      }

      if (deliveryCondition.delivery_type === 'delivery_conditions') {
        if (deliveryCondition.is_all_products) {
          return listingProducts;
        }

        if (
          deliveryCondition.products &&
          deliveryCondition.products.length > 0
        ) {
          return deliveryCondition.products
            .map((product) => {
              if (
                'listing_product_id' in product &&
                product.listing_product_id
              ) {
                const deliveryProduct = product as DeliveryProductReference;
                const productId = deliveryProduct.listing_product_id;
                if (!productId) return undefined;
                const listingProduct = listingProducts.find(
                  (p) => p.id === productId
                );

                if (listingProduct) {
                  return {
                    ...listingProduct,
                    required_quantity: parseFloat(
                      deliveryProduct.product_quantity?.toString() ||
                        listingProduct.required_quantity?.toString() ||
                        '0'
                    ),
                  };
                }
                return undefined;
              } else {
                return product as ListingProduct;
              }
            })
            .filter((p): p is ListingProduct => p !== undefined);
        }
      }

      return listingProducts;
    },
    [listingProducts]
  );

  const shouldShowAddDeliverySection = useMemo(() => {
    const deliveries = formDeliveries || [];
    const hasUnfulfilled = productQuantities.some(
      (pq) => pq.remainingQuantity > 0
    );

    if (isEditing && deliveries.length > 0) {
      const allAcceptingAll = deliveries.every(
        (delivery) => delivery.is_accepting_all
      );
      if (allAcceptingAll) {
        return false;
      }
    }

    return hasUnfulfilled;
  }, [productQuantities, isEditing, formDeliveries]);

  const handleDeliveryTypeChange = useCallback(
    (index: number, value: 'all_acceptable' | 'custom') => {
      const isChangingToAcceptAll = value === 'all_acceptable';
      const deliveries =
        (form.getFieldValue(
          'delivery_conditions'
        ) as ApplicationDeliveryCondition[]) || [];
      const currentDelivery = deliveries[index];

      form.setFieldValue(
        ['delivery_conditions', index, 'is_accepting_all'],
        value === 'all_acceptable'
      );

      if (isChangingToAcceptAll) {
        if (!currentDelivery?.listing_delivery_condition_id) {
          const tempDeliveries = [...deliveries];
          tempDeliveries[index] = {
            ...currentDelivery,
            is_accepting_all: true,
            products: [],
          };

          const tempQuantities = calculateProductQuantities(
            listingProducts,
            availableProducts,
            tempDeliveries.filter((_, idx) => idx !== index)
          );

          const productsWithRemaining: DeliveryProductReference[] =
            tempQuantities
              .filter((pq) => pq.remainingQuantity > 0)
              .map((pq) => ({
                id: undefined,
                listing_product_id: pq.listing_product_id,
                product_quantity: pq.remainingQuantity,
              }));

          form.setFieldValue(
            ['delivery_conditions', index, 'products'],
            productsWithRemaining
          );
        } else {
          const filteredDeliveries = deliveries.filter((delivery, idx) => {
            if (idx === index) return true;
            const hasListingConditionId =
              delivery.listing_delivery_condition_id !== undefined &&
              delivery.listing_delivery_condition_id !== null;
            return hasListingConditionId;
          });

          const updatedDeliveries = filteredDeliveries.map((delivery, idx) => {
            if (idx === index) {
              return {
                ...delivery,
                is_accepting_all: true,
                products: [],
              };
            }
            return delivery;
          });

          form.setFieldValue('delivery_conditions', updatedDeliveries);
        }
      } else {
        form.setFieldValue(['delivery_conditions', index, 'products'], []);
      }
    },
    [form, listingProducts, availableProducts, calculateProductQuantities]
  );

  const validateProductQuantity = useCallback(
    (
      productId: string,
      newQuantity: number,
      deliveryIndex: number
    ): boolean => {
      const productQuantity = productQuantities.find(
        (pq) => pq.listing_product_id === productId
      );

      if (!productQuantity) {
        return true;
      }

      const deliveries = formDeliveries || [];
      const currentDelivery = deliveries[deliveryIndex];
      const currentProductInDelivery = currentDelivery?.products?.find(
        (p) => p.listing_product_id === productId
      );
      const currentQuantityInDelivery = currentProductInDelivery
        ? parseFloat(
            currentProductInDelivery.product_quantity?.toString() || '0'
          )
        : 0;

      const availableRemaining =
        productQuantity.remainingQuantity + currentQuantityInDelivery;

      if (newQuantity > availableRemaining) {
        message.error(
          intl.formatMessage(
            { id: 'validation.quantity_exceeds_available' },
            {
              available: availableRemaining,
              unit: productQuantity.product.storage_unit?.short || 'kg',
            }
          )
        );
        return false;
      }

      return true;
    },
    [productQuantities, formDeliveries, intl]
  );

  const handleProductVolumeChange = useCallback(
    (deliveryIndex: number, productId: string, volume: number) => {
      const deliveries = formDeliveries || [];
      const d = deliveries[deliveryIndex];
      if (!d) return;

      const products: DeliveryProductReference[] = Array.isArray(d.products)
        ? [...d.products]
        : [];
      const idx = products.findIndex((p) => p.listing_product_id === productId);

      if (volume === 0 || isNaN(volume)) {
        if (idx >= 0) {
          products.splice(idx, 1);
        }
      } else {
        if (!validateProductQuantity(productId, volume, deliveryIndex)) {
          return;
        }
        if (idx >= 0) {
          products[idx] = { ...products[idx], product_quantity: volume };
        } else {
          products.push({
            id: undefined,
            listing_product_id: productId,
            product_quantity: volume,
          });
        }
      }

      form.setFieldValue(
        ['delivery_conditions', deliveryIndex, 'products'],
        products
      );
    },
    [formDeliveries, form, validateProductQuantity]
  );

  const addAdditionalDelivery = useCallback(() => {
    const deliveries = formDeliveries || [];

    const tempQuantities = calculateProductQuantities(
      listingProducts,
      availableProducts,
      deliveries
    );

    const productsWithRemaining = tempQuantities
      .filter((pq) => pq.remainingQuantity > 0 && pq.listing_product_id)
      .map((pq) => ({
        listing_product_id: pq.listing_product_id!,
        product_quantity: pq.remainingQuantity,
      }));

    const newDelivery: ApplicationDeliveryCondition = {
      delivery_start_date: undefined,
      delivery_end_date: undefined,
      delivery_price: undefined,
      delivery_frequency_value: undefined,
      delivery_frequency_unit: undefined,
      is_accepting_all: true,
      products: productsWithRemaining,
    };
    form.setFieldValue('delivery_conditions', [...deliveries, newDelivery]);
  }, [
    formDeliveries,
    form,
    listingProducts,
    availableProducts,
    calculateProductQuantities,
  ]);

  const removeDelivery = useCallback(
    (index: number) => {
      const deliveries = formDeliveries || [];
      const updatedDeliveries = deliveries.filter((_, idx) => idx !== index);
      form.setFieldValue('delivery_conditions', updatedDeliveries);
    },
    [formDeliveries, form]
  );

  const getDeliveryFrequency = (
    condition: DeliveryConditionsData | DeliveryConditionType
  ): string => {
    if (!condition) return '';

    if (
      condition.delivery_frequency_value &&
      condition.delivery_frequency_unit
    ) {
      return `${condition.delivery_frequency_value} ${intl.formatMessage({
        id: `enum.${condition.delivery_frequency_unit.toUpperCase()}`,
      })}`;
    }

    return '';
  };

  return (
    <StyledApplicationDeliveryForm>
      {/* Free Form Delivery Description */}
      {freeFormDeliveryCondition && (
        <div className="free-form-delivery-section">
          <p>{freeFormDeliveryCondition.free_form_description}</p>
        </div>
      )}

      {/* Delivery Cards */}
      <Form.List name="delivery_conditions">
        {(fields) => {
          return (
            <div className="deliveries-list">
              {fields.map((field, index) => {
                const delivery = form.getFieldValue([
                  'delivery_conditions',
                  field.name,
                ]) as ApplicationDeliveryCondition | undefined;
                if (!delivery) {
                  return null;
                }

                const listingCondition = listingDeliveryConditions.find(
                  (condition) =>
                    condition.id === delivery.listing_delivery_condition_id
                );

                return (
                  <div key={field.key} className="delivery-card">
                    <div className="delivery-header">
                      <Label label={index + 1} className="delivery-number" />
                      {listingCondition ? (
                        <div className="delivery-schedule-info">
                          {listingCondition.delivery_type ===
                            'detailed_delivery' && (
                            <>
                              {listingCondition.delivery_start_date &&
                                listingCondition.delivery_end_date && (
                                  <Label
                                    className="date-range"
                                    label={formatDateRange(
                                      listingCondition.delivery_start_date,
                                      listingCondition.delivery_end_date
                                    )}
                                  />
                                )}
                              {getDeliveryFrequency(listingCondition) && (
                                <Label
                                  className="frequency"
                                  label={`${intl.formatMessage({
                                    id: 'listings.application.delivery_frequency',
                                  })}: ${getDeliveryFrequency(listingCondition)}`}
                                />
                              )}
                              {(listingCondition.delivery_address ||
                                listingData?.address) && (
                                <Label
                                  className="delivery-address"
                                  label={
                                    listingCondition.delivery_address ||
                                    listingData?.address ||
                                    ''
                                  }
                                />
                              )}
                            </>
                          )}

                          {listingCondition.delivery_type ===
                            'delivery_conditions' && (
                            <>
                              {listingCondition.contract_start_date &&
                                listingCondition.contract_end_date && (
                                  <Label
                                    className="date-range"
                                    label={formatDateRange(
                                      listingCondition.contract_start_date,
                                      listingCondition.contract_end_date
                                    )}
                                  />
                                )}
                              {getDeliveryFrequency(listingCondition) && (
                                <Label
                                  className="frequency"
                                  label={`${intl.formatMessage({
                                    id: 'listings.application.delivery_frequency',
                                  })}: ${getDeliveryFrequency(listingCondition)}`}
                                />
                              )}
                              {listingCondition.delivery_period_value &&
                                listingCondition.delivery_period_unit && (
                                  <Label
                                    className="period"
                                    label={`${intl.formatMessage({
                                      id: 'listings.application.delivery_period',
                                    })}: ${listingCondition.delivery_period_value} ${listingCondition.delivery_period_unit}`}
                                  />
                                )}
                              {(listingCondition.delivery_address ||
                                listingData?.address) && (
                                <Label
                                  className="delivery-address"
                                  label={
                                    listingCondition.delivery_address ||
                                    listingData?.address ||
                                    ''
                                  }
                                />
                              )}
                            </>
                          )}
                        </div>
                      ) : (
                        (delivery.delivery_start_date ||
                          delivery.delivery_end_date ||
                          delivery.delivery_frequency_value) && (
                          <div className="delivery-schedule-info">
                            <Label
                              className="additional-delivery"
                              label={intl.formatMessage({
                                id: 'listings.application.additional_delivery',
                              })}
                            />
                          </div>
                        )
                      )}
                    </div>

                    <div className="delivery-radio-section">
                      <Form.Item
                        shouldUpdate={(prev, curr) => {
                          const prevValue =
                            prev?.delivery_conditions?.[index]
                              ?.is_accepting_all;
                          const currValue =
                            curr?.delivery_conditions?.[index]
                              ?.is_accepting_all;
                          return prevValue !== currValue;
                        }}
                      >
                        {() => {
                          const isAcceptingAll =
                            form.getFieldValue([
                              'delivery_conditions',
                              index,
                              'is_accepting_all',
                            ]) || false;
                          return (
                            <Radio.Group
                              value={
                                isAcceptingAll ? 'all_acceptable' : 'custom'
                              }
                              onChange={(e) =>
                                handleDeliveryTypeChange(index, e.target.value)
                              }
                            >
                              <div className="radio-options">
                                <Radio value="all_acceptable">
                                  {intl.formatMessage({
                                    id: 'listings.application.everything_acceptable',
                                  })}
                                </Radio>
                                <Radio value="custom">
                                  {intl.formatMessage({
                                    id: 'listings.application.other_delivery_options',
                                  })}
                                </Radio>
                              </div>
                            </Radio.Group>
                          );
                        }}
                      </Form.Item>
                      <Form.Item
                        name={[field.name, 'is_accepting_all']}
                        initialValue={false}
                        hidden
                      >
                        <input type="hidden" />
                      </Form.Item>
                      <Divider />
                    </div>

                    <Form.Item
                      shouldUpdate={(prev, curr) => {
                        const prevValue =
                          prev?.delivery_conditions?.[index]?.is_accepting_all;
                        const currValue =
                          curr?.delivery_conditions?.[index]?.is_accepting_all;
                        return prevValue !== currValue;
                      }}
                    >
                      {() => {
                        const isAcceptingAll =
                          form.getFieldValue([
                            'delivery_conditions',
                            index,
                            'is_accepting_all',
                          ]) || false;
                        return isAcceptingAll ? (
                          <div className="delivery-content">
                            <div className="products">
                              <Label
                                label={intl.formatMessage({
                                  id: 'listings.application.all_products',
                                })}
                                title={true}
                              />
                              {(() => {
                                if (listingCondition) {
                                  return getProductsForDelivery(
                                    listingCondition || null
                                  )
                                    .filter(
                                      (p): p is ListingProduct =>
                                        p !== undefined
                                    )
                                    .map(
                                      (
                                        product: ListingProduct,
                                        productIndex: number
                                      ) => {
                                        const applicationProduct =
                                          availableProducts.find(
                                            (ap) =>
                                              ap.listing_product_id ===
                                              product.id
                                          );
                                        const displayQuantity =
                                          getAvailableProductQuantity(
                                            product,
                                            applicationProduct
                                          );

                                        return (
                                          <Label
                                            key={productIndex}
                                            label={`${getProductDisplayName(product, product.id, availableProducts, farmProductNames)} - (${displayQuantity} ${product.storage_unit?.short || ''}.)`}
                                          />
                                        );
                                      }
                                    );
                                } else {
                                  if (
                                    delivery?.products &&
                                    delivery.products.length > 0
                                  ) {
                                    return delivery.products
                                      .map((p, idx) => {
                                        const product = listingProducts.find(
                                          (lp) => lp.id === p.listing_product_id
                                        );
                                        if (!product) return null;

                                        return (
                                          <Label
                                            key={idx}
                                            label={`${getProductDisplayName(product, p.listing_product_id, availableProducts, farmProductNames)} - (${p.product_quantity} ${product.storage_unit?.short || ''}.)`}
                                          />
                                        );
                                      })
                                      .filter(Boolean);
                                  }

                                  return null;
                                }
                              })()}
                            </div>
                          </div>
                        ) : (
                          <div className="delivery-content">
                            <div className="selected-products">
                              {getProductsForDelivery(null)
                                .filter(
                                  (p): p is ListingProduct => p !== undefined
                                )
                                .map((product: ListingProduct) => {
                                  const productQuantity =
                                    productQuantities.find(
                                      (pq) =>
                                        pq.listing_product_id === product.id
                                    );
                                  const remainingQuantity = productQuantity
                                    ? isNaN(productQuantity.remainingQuantity)
                                      ? 0
                                      : productQuantity.remainingQuantity
                                    : 0;

                                  return (
                                    <div
                                      key={product.id}
                                      className="product-quantity-row"
                                    >
                                      <div className="product-info">
                                        <span className="product-name">
                                          {getProductDisplayName(
                                            product,
                                            product.id,
                                            availableProducts,
                                            farmProductNames
                                          )}
                                        </span>
                                      </div>
                                      <div className="quantity-input">
                                        <Label
                                          label={intl.formatMessage(
                                            {
                                              id: 'listings.application.total_quantity',
                                            },
                                            {
                                              required_quantity:
                                                remainingQuantity,
                                              unit_short:
                                                product.storage_unit?.short ||
                                                product.storage_unit?.name ||
                                                '',
                                            }
                                          )}
                                        />
                                        <Form.Item shouldUpdate>
                                          {() => {
                                            const delivery = form.getFieldValue(
                                              ['delivery_conditions', index]
                                            ) as
                                              | ApplicationDeliveryCondition
                                              | undefined;
                                            const productInDelivery =
                                              delivery?.products?.find(
                                                (p) =>
                                                  p.listing_product_id ===
                                                  product.id
                                              );
                                            const quantity =
                                              productInDelivery?.product_quantity;
                                            return (
                                              <Input
                                                className="delivery-quantity-input"
                                                key={`product-${index}-${product.id}`}
                                                type="number"
                                                required={true}
                                                disabled={false}
                                                value={quantity || undefined}
                                                validations="positiveNumber"
                                                placeholder={intl.formatMessage(
                                                  {
                                                    id: 'listings.form.input_delivery_quantity',
                                                  },
                                                  {
                                                    unit_short:
                                                      product.storage_unit
                                                        ?.short ||
                                                      product.storage_unit
                                                        ?.name ||
                                                      '',
                                                  }
                                                )}
                                                onChange={(e) => {
                                                  if (product.id) {
                                                    const value =
                                                      e.target.value;
                                                    if (
                                                      value === '' ||
                                                      value === null ||
                                                      value === undefined
                                                    ) {
                                                      handleProductVolumeChange(
                                                        index,
                                                        product.id,
                                                        0
                                                      );
                                                      return;
                                                    }
                                                    const parsedValue =
                                                      parseFloat(value);
                                                    if (
                                                      !isNaN(parsedValue) &&
                                                      parsedValue >= 0
                                                    ) {
                                                      handleProductVolumeChange(
                                                        index,
                                                        product.id,
                                                        parsedValue
                                                      );
                                                    }
                                                  }
                                                }}
                                              />
                                            );
                                          }}
                                        </Form.Item>
                                      </div>
                                    </div>
                                  );
                                })}
                            </div>

                            {/* Editable delivery form */}
                            <div className="row">
                              <div className="frequency-and-unit">
                                <Input
                                  name={[
                                    field.name,
                                    'delivery_frequency_value',
                                  ]}
                                  className="frequency-input"
                                  type="number"
                                  label={intl.formatMessage({
                                    id: 'listings.application.delivery_frequency',
                                  })}
                                  validations={['required', 'positiveNumber']}
                                  placeholder={intl.formatMessage({
                                    id: 'listings.application.delivery_frequency_placeholder',
                                  })}
                                />
                                <EnumSelect
                                  name={[field.name, 'delivery_frequency_unit']}
                                  code="ListingDeliveryFrequencyUnit"
                                  validations="required"
                                />
                              </div>
                            </div>

                            <div className="row">
                              <div className="delivery-start-date">
                                <DatePicker
                                  name={[field.name, 'delivery_start_date']}
                                  label={intl.formatMessage({
                                    id: 'listings.application.delivery_start_date',
                                  })}
                                  validations="required"
                                  format="DD.MM.YYYY"
                                  placeholder={intl.formatMessage({
                                    id: 'listings.application.delivery_start_date_placeholder',
                                  })}
                                />
                              </div>
                              <div className="delivery-end-date">
                                <DatePicker
                                  name={[field.name, 'delivery_end_date']}
                                  label={intl.formatMessage({
                                    id: 'listings.application.delivery_end_date',
                                  })}
                                  validations="required"
                                  format="DD.MM.YYYY"
                                  placeholder={intl.formatMessage({
                                    id: 'listings.application.delivery_end_date_placeholder',
                                  })}
                                  rules={[
                                    ({ getFieldValue }) => ({
                                      validator(_, value) {
                                        const startDate = getFieldValue([
                                          'delivery_conditions',
                                          field.name,
                                          'delivery_start_date',
                                        ]);
                                        if (!value || !startDate) {
                                          return Promise.resolve();
                                        }
                                        if (
                                          value.isBefore(startDate) ||
                                          value.isSame(startDate)
                                        ) {
                                          return Promise.reject(
                                            new Error(
                                              intl.formatMessage({
                                                id: 'validation.end_date_after_start',
                                              })
                                            )
                                          );
                                        }
                                        return Promise.resolve();
                                      },
                                    }),
                                  ]}
                                />
                              </div>
                            </div>
                          </div>
                        );
                      }}
                    </Form.Item>

                    {/* Delivery price input */}
                    <div className="delivery-price">
                      <InputNumber
                        name={[field.name, 'delivery_price']}
                        min={0}
                        controls={false}
                        label={intl.formatMessage({
                          id: 'listings.application.delivery_price_eur',
                        })}
                        validations={['required', 'minZeroNumber']}
                      />
                    </div>
                    {!listingCondition && (
                      <div className="delivery-actions">
                        <Button
                          label={intl.formatMessage({ id: 'general.delete' })}
                          onClick={() => removeDelivery(index)}
                          type="primary"
                        />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          );
        }}
      </Form.List>

      {/* Additional Delivery Section */}
      {shouldShowAddDeliverySection && (
        <div className="additional-delivery-section">
          <Label
            label={intl.formatMessage({
              id: 'listings.application.quantity_differs',
            })}
          />
          <Button
            className="action-button"
            label={intl.formatMessage({
              id: 'listings.application.add_additional_delivery',
            })}
            onClick={addAdditionalDelivery}
          />
        </div>
      )}
    </StyledApplicationDeliveryForm>
  );
};
